import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { LockReset as LockResetIcon, Delete as DeleteIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { SideDrawer, EmptyState } from './primitives';
import RoleChip from '../RoleChip';
import { isAdminRole, assignableRoles, ROLES } from '../../constants/roles';
import WeeklySchedule, { groupScheduleByDay } from './WeeklySchedule';
import AccountSection from './AccountSection';
import ResetPasswordDialog from './ResetPasswordDialog';
import { staffTierGuard } from './adminGuards';

// Staff detail panel — GET /api/admin/users?username=<username> (an exact,
// case-insensitive match — there's no GET-by-id endpoint for a single user)
// plus, for a TEACHER target only, GET /api/users/{id}/groups for their
// teaching schedule. Routed at /admin/staff/:username by StaffPage, which
// never unmounts across this drawer opening/closing.
//
// Sibling of StudentDrawer.js, sharing its Weekly Schedule rendering,
// Account (role/school/access) block, and one-time reset-password dialog
// (see WeeklySchedule.js/AccountSection.js/ResetPasswordDialog.js). Only
// ever mounted by StaffPage, which is itself only reachable by an admin
// role (see AdminDashboardV2.js's `staff/:username?` route guard) — so
// unlike StudentDrawer, there's no non-admin viewer case to render a
// read-only version for; `isAdminViewer` below is a defense-in-depth
// belt-and-suspenders check, not a real branch a teacher can reach.
//
// Deliberately narrower than StudentDrawer: no status chip/"last active"
// (derived from tutoring-message activity, meaningless for staff — see
// StaffPage.js's header comment), no class add/remove list (a teacher's
// classes are assigned from ClassesPage's own detail pane, not from here —
// this drawer only *shows* them), no "View conversations" footer action (staff
// don't have tutoring conversations of their own).
export default function StaffDrawer({
  username,
  onClose,
  currentUserRole,
  currentUser,
  notify,
  confirmAction,
  onRoleChange,
  onSchoolChange,
  onAccessToggle,
  onDeleteUser,
  onResetPassword,
  onMutated,
}) {
  const navigate = useNavigate();
  const open = Boolean(username);

  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [user, setUser] = useState(null);

  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const [resetPasswordResult, setResetPasswordResult] = useState(null); // { user, temporaryPassword } | null
  const [passwordCopied, setPasswordCopied] = useState(false);

  const loadSchedule = useCallback(
    async (userId, signal) => {
      setScheduleLoading(true);
      setScheduleError(null);
      try {
        const res = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/groups`, {}, { signal });
        const data = await handleApiResponse(res, navigate);
        if (data === null || signal?.aborted) return; // 401 — already redirected
        setScheduleGroups(data.groups || []);
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted) return;
        setScheduleError(describeAdminError(err, "Couldn't load this teacher's schedule."));
      } finally {
        if (!signal?.aborted) setScheduleLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!username) {
      setUser(null);
      setNotFound(false);
      setLoadError(null);
      setScheduleGroups([]);
      return undefined;
    }
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setNotFound(false);
      setLoadError(null);
      setScheduleGroups([]);
      try {
        const params = new URLSearchParams({ username, page_size: '1' });
        const res = await authFetch(`${config.API_BASE_URL}/api/admin/users?${params.toString()}`, {}, { signal: controller.signal });
        const data = await handleApiResponse(res, navigate);
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        const match = (data.users || [])[0] || null;
        if (!match) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setUser(match);
        setLoading(false);
        // Only a TEACHER has a teaching schedule to show — a school/master
        // admin row skips this fetch entirely rather than making a request
        // that would just come back empty.
        if (match.role === ROLES.TEACHER) {
          loadSchedule(match.id, controller.signal);
        }
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setLoadError(describeAdminError(err, "Couldn't load this staff member."));
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [username, navigate, loadSchedule]);

  const refreshUser = useCallback(async () => {
    if (!user) return;
    const controller = new AbortController();
    try {
      const params = new URLSearchParams({ username: user.username, page_size: '1' });
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/users?${params.toString()}`, {}, { signal: controller.signal });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return;
      const match = (data.users || [])[0];
      if (match) setUser(match);
    } catch {
      // Best-effort refresh after a role/school/access edit — the toast
      // from that action already reported success/failure; a stale drawer
      // field until the next open isn't worth a second error surface.
    }
  }, [user, navigate]);

  const isAdminViewer = isAdminRole(currentUserRole);
  const pickableRoles = assignableRoles(currentUserRole);
  const isTeacherTarget = user?.role === ROLES.TEACHER;

  const scheduleByDay = useMemo(() => groupScheduleByDay(scheduleGroups), [scheduleGroups]);

  const handleRoleSelect = async (newRole) => {
    await onRoleChange(user.id, newRole);
    onMutated?.();
    refreshUser();
  };

  const handleSchoolSelect = async (newSchool) => {
    await onSchoolChange(user.id, newSchool);
    onMutated?.();
    refreshUser();
  };

  const handleAccessClick = async () => {
    await onAccessToggle(user.id, user.access_granted !== false);
    onMutated?.();
    refreshUser();
  };

  const handleResetPasswordClick = () => {
    confirmAction({
      title: 'Reset Password',
      body: `Generate a temporary password for ${user.username}? Any existing reset links will stop working.`,
      confirmLabel: 'Generate Password',
      onConfirm: performResetPassword,
    });
  };

  const performResetPassword = async () => {
    try {
      const data = await onResetPassword(user.id);
      if (data === null) return; // 401 — already redirected
      setPasswordCopied(false);
      setResetPasswordResult({ user, temporaryPassword: data.temporary_password });
    } catch (err) {
      notify(`This password couldn't be reset. ${describeAdminError(err)}`, 'error');
    }
  };

  const closeResetPasswordResult = () => {
    setResetPasswordResult(null);
    setPasswordCopied(false);
  };

  const copyTemporaryPassword = async () => {
    const text = resetPasswordResult?.temporaryPassword || '';
    if (!text) return;
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(text);
      setPasswordCopied(true);
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const succeeded = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (succeeded) setPasswordCopied(true);
        else notify('Copy failed — select the password above and copy it manually.', 'warning');
      } catch (fallbackErr) {
        console.error('Copy to clipboard failed:', fallbackErr);
        notify('Copy failed — select the password above and copy it manually.', 'warning');
      }
    }
  };

  const handleDeleteClick = () => {
    confirmAction({
      title: 'Delete User',
      body: `Delete user "${user.username}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        await onDeleteUser(user);
        onMutated?.();
        onClose();
      },
    });
  };

  let body;
  if (loading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (notFound) {
    body = (
      <EmptyState
        title="Staff member not found"
        description="They may have been removed, or you may not have access to them."
        action={
          <Button variant="outlined" onClick={onClose} sx={TOUCH_HIT_SX}>
            Back to Staff
          </Button>
        }
      />
    );
  } else if (loadError) {
    body = <Alert severity="error">{loadError}</Alert>;
  } else if (user) {
    body = (
      <Box>
        {/* Header info: role + email — SideDrawer's own title/subtitle
            already carry name + school. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, mb: 3 }}>
          <RoleChip role={user.role} />
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            {user.email || 'No email on file'}
          </Typography>
        </Box>

        {isTeacherTarget && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <ScheduleIcon sx={{ fontSize: 18, color: adminColors.inkSoft }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink }}>
                Weekly Schedule
              </Typography>
            </Box>
            <WeeklySchedule
              ownerName={user.username}
              scheduleByDay={scheduleByDay}
              loading={scheduleLoading}
              error={scheduleError}
              variant="teacher"
            />
          </>
        )}

        {/* Account — role/school/access. AccountSection supplies its own
            leading Divider, so this never needs one of its own whether or
            not the Weekly Schedule section above rendered. */}
        {isAdminViewer && (
          <AccountSection
            user={user}
            currentUserRole={currentUserRole}
            currentUser={currentUser}
            pickableRoles={pickableRoles}
            onRoleSelect={handleRoleSelect}
            onSchoolSelect={handleSchoolSelect}
            onAccessClick={handleAccessClick}
          />
        )}
      </Box>
    );
  }

  return (
    <>
      <SideDrawer
        open={open}
        onClose={onClose}
        // Prefer the route param over `user.username` — same staleness
        // guard StudentDrawer's title/subtitle use, see its comment for why.
        title={username || user?.username || 'Staff'}
        subtitle={!loading && user ? user.school || 'No school assigned' : undefined}
        width={520}
        footer={
          !loading &&
          user &&
          isAdminViewer && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={handleResetPasswordClick}
                startIcon={<LockResetIcon fontSize="small" />}
                disabled={staffTierGuard(currentUserRole, user.role)}
                sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft }}
              >
                Reset password
              </Button>
              <Button
                size="small"
                color="error"
                onClick={handleDeleteClick}
                startIcon={<DeleteIcon fontSize="small" />}
                disabled={staffTierGuard(currentUserRole, user.role)}
                sx={TOUCH_HIT_SX}
              >
                Delete
              </Button>
            </Box>
          )
        }
      >
        {body}
      </SideDrawer>

      <ResetPasswordDialog
        username={resetPasswordResult?.user?.username}
        temporaryPassword={resetPasswordResult?.temporaryPassword}
        hasEmail={Boolean(resetPasswordResult?.user?.email)}
        copied={passwordCopied}
        onCopy={copyTemporaryPassword}
        onClose={closeResetPasswordResult}
      />
    </>
  );
}
